import { motion } from 'framer-motion';
import { Play, CheckCircle2, ChevronLeft, ChevronRight, Star, Monitor, ShoppingCart, Shield, TrendingUp, Users, Video, Target, Wrench, Eye } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, query, orderBy, onSnapshot, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { blogContent } from '../data/blogContent';
import { localizedRoutesMap } from '../routes';

// Types from Firebase
interface Project {
  id: string;
  title: string;
  url: string;
  category: string;
  description: string;
  thumbnail: string;
  projectType?: 'live' | 'demo';
  ownerId: string;
  ownerName?: string;
  likes?: string[];
  views?: number;
  seoScore?: number;
  createdAt: any;
}


export const BrandsStrip = () => {
  const { t } = useTranslation();
  return (
  <div className="py-12 border-t border-white/5 bg-near-black/70 overflow-hidden relative">
    <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center gap-6 lg:gap-12">
      <span className="text-white/80 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">{t('socialProof.trustedBy')}</span>
      <div className="flex items-center gap-8 sm:gap-16 opacity-40 hover:opacity-100 grayscale hover:grayscale-0 transition-all duration-500 overflow-x-auto custom-scrollbar pb-2 sm:pb-0 w-full justify-between lg:justify-start">
        <span className="font-display font-bold text-xl tracking-wide">BINNI</span>
        <span className="font-display font-medium text-xl tracking-widest flex items-center gap-2"><div className="w-5 h-5 bg-white rounded-full flex items-center justify-center text-near-black text-xs font-bold">X</div> MERIDIAN</span>
        <span className="font-display italic text-2xl lowercase tracking-tighter">vala</span>
        <span className="font-display text-xl tracking-tight flex items-center gap-1"><div className="w-4 h-4 border border-current rounded-full"></div> FOURTEEN</span>
        <span className="font-display font-bold text-xl uppercase font-serif tracking-widest">KEDS</span>
        <span className="font-display font-black text-xl italic">ATV</span>
        <span className="font-display text-lg lowercase tracking-tight">kujtesa</span>
      </div>
    </div>
  </div>
)};

export const SelectedProjects = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const navigate = useNavigate();
  // Using categories from i18n
  const trackerT = t('tracker.filters', { returnObjects: true }) as any;
  const categories = Object.entries(trackerT);

  const filteredProjects = (activeCategory === 'all' 
    ? projects 
    : projects.filter(p => p.category.toLowerCase() === activeCategory.toLowerCase())).slice(0, 3);

  useEffect(() => {
    const q = query(
      collection(db, 'projects'),
      orderBy('createdAt', 'desc')
      // Removed limit(3) to show all projects for filtering
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedProjects = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Project));
      setProjects(fetchedProjects);
    });
    return () => unsubscribe();
  }, []);

  return (
    <div className="bg-near-black">
      <section id="portfolio" className="py-24 border-t border-white/5 bg-near-black/70 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6 relative z-10 w-full">
            <div>
              <p className="text-primary-light text-xs font-bold uppercase tracking-widest mb-2">{t('work.portfolio.title')}</p>
              <h2 className="text-3xl md:text-5xl font-display font-bold" dangerouslySetInnerHTML={{ __html: t('work.portfolio.subtitle').replace(' ', '<br/>') }} />
            </div>
            <button onClick={() => navigate('/templates')} className="border border-white/20 hover:bg-white/5 px-6 py-2 rounded-full text-xs transition-all text-white/80">{t('work.portfolio.viewAll')} &gt;</button>
          </div>

          <div className="flex justify-center gap-2 md:gap-4 mb-12 overflow-x-auto pb-4 custom-scrollbar relative z-10 w-full">
            {categories.map(([key, value]) => (
              <button 
                key={key} 
                onClick={() => setActiveCategory(key)}
                className={`px-6 py-2 rounded-full text-xs whitespace-nowrap transition-all ${activeCategory === key ? 'bg-primary text-white border border-primary' : 'bg-charcoal/50 text-white/80 hover:text-white border border-white/5'}`}
              >
                {value}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {filteredProjects.map((project, index) => (
              <motion.div 
                key={project.id} 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (index % 3) * 0.1 }}
                onClick={() => navigate('/templates?t=' + project.id)}
                className="p-6 rounded-2xl border border-white/10 hover:border-primary-light/50 bg-charcoal/40 cursor-pointer transition-all relative group"
              >
                <div className="aspect-video rounded-lg overflow-hidden mb-4 relative">
                  <img src={project.thumbnail} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-near-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    <Eye className="text-white" />
                  </div>
                </div>
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold text-lg group-hover:text-primary-light transition-colors">{project.title}</h3>
                </div>
                <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest font-bold text-white/80">
                  <span>{project.category}</span>
                </div>
              </motion.div>
            ))}
            {filteredProjects.length === 0 && (
              <div className="col-span-full text-center text-white/80 italic py-12">{t('tracker.noProjects')}</div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};


export const WhyChooseUs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const whyChooseUs = t('about.whyChooseUs', { returnObjects: true }) as any || { title: '', desc: '', items: [], cta: '' };
  return (
  <section className="py-24 bg-near-black/70 border-t border-white/5 relative overflow-hidden">
    <div className="w-[800px] h-[800px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary/10 rounded-full blur-[150px] pointer-events-none"></div>
    <div className="max-w-7xl mx-auto px-6 relative z-10 w-full">
      <div className="flex flex-col lg:flex-row items-center gap-16 w-full">
        <div className="lg:w-[45%]">
            <p className="text-primary-light text-xs font-bold uppercase tracking-widest mb-4">{t('about.title')}</p>
            <h2 className="text-3xl md:text-5xl font-display font-bold mb-6 leading-tight">{whyChooseUs.title}</h2>
            <p className="text-white/80 mb-8 italic text-sm">{whyChooseUs.desc}</p>
          
          <div className="flex flex-col gap-4 mb-10">
            {whyChooseUs.items.map((item: string, i: number) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                  <CheckCircle2 size={12} className="text-primary-light" />
                </div>
                <span className="text-sm text-white/80">{item}</span>
              </div>
            ))}
          </div>
          
          <button 
            onClick={() => navigate('/about')}
            className="bg-primary hover:bg-primary-light text-white px-8 py-3 rounded-xl font-bold text-sm transition-all shadow-[0_0_20px_rgba(139,92,246,0.3)]"
          >
            {whyChooseUs.cta}
          </button>
        </div>
        
        <div className="lg:w-[55%] w-full relative">
          <div className="grid grid-cols-2 gap-4">
             {/* Large top image */}
             <div className="col-span-2 h-64 bg-charcoal/50 rounded-3xl overflow-hidden border border-white/10 relative">
               <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=60&w=640&auto=format&fit=crop" className="w-full h-full object-cover opacity-80 mix-blend-screen" alt="Team" loading="lazy" />
               <div className="absolute inset-0 bg-primary/10 mix-blend-overlay"></div>
             </div>
             {/* Bottom left */}
             <div className="h-48 bg-charcoal/50 rounded-3xl overflow-hidden border border-white/10">
               <img src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=60&w=640&auto=format&fit=crop" className="w-full h-full object-cover opacity-70 mix-blend-saturation" alt="Coding" loading="lazy" />
             </div>
             {/* Bottom right */}
             <div className="h-48 bg-charcoal/50 rounded-3xl overflow-hidden border border-white/10">
               <img src="https://images.unsplash.com/photo-1531482615713-2afd69097998?q=60&w=640&auto=format&fit=crop" className="w-full h-full object-cover opacity-70 mix-blend-saturation" alt="Reviewing" loading="lazy" />
             </div>
          </div>
          
          <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 bg-near-black/80 backdrop-blur-xl border border-white/10 border-primary/30 p-6 rounded-3xl shadow-[0_10px_40px_rgba(139,92,246,0.15)] flex flex-col items-center">
             <span className="text-5xl font-display font-medium text-white mb-1">{t('about.experience.years')}</span>
             <span className="text-[10px] uppercase text-white/80 text-center tracking-widest leading-tight">{t('about.experience.desc')}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
)};
export const Process = () => {
  const { t } = useTranslation();
  const processT = t('work.process.steps', { returnObjects: true });
  const steps = (Array.isArray(processT) ? processT : (t('process.steps', { returnObjects: true }) as any[])) || [];
  return (
  <section className="py-24 bg-near-black/70 relative overflow-hidden">
    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none"></div>
    <div className="max-w-4xl mx-auto px-6 relative z-10 w-full">
      <div className="text-center mb-20">
        <p className="text-primary-light text-xs font-bold uppercase tracking-widest mb-2">{t('process.title') || t('work.process.title')}</p>
        <h2 className="text-3xl md:text-5xl font-display font-bold">{t('work.process.title') || t('process.title')}</h2>
      </div>
      
      <div className="space-y-12">
        {steps.map((step: any, i: number) => (
          <motion.div 
            key={i} 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className={`flex flex-col md:flex-row items-center gap-8 ${i % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}
          >
            <div className="w-full md:w-1/2 flex flex-col items-center md:items-start text-center md:text-left">
              <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-primary-light font-display text-xl font-bold mb-6 shadow-inner relative overflow-hidden">
                 <div className="absolute inset-0 bg-primary-light/20 blur-md opacity-20"></div>
                 {String(i + 1).padStart(2, '0')}
              </div>
              <h3 className="text-2xl font-bold mb-3">{step.title}</h3>
              <p className="text-white/80 leading-relaxed">{step.desc}</p>
            </div>
            <div className="hidden md:flex w-full md:w-1/2 justify-center">
              <div className="w-1/2 h-[1px] bg-gradient-to-r from-transparent via-primary-light/50 to-transparent"></div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
)};



export const Testimonials = () => {
  const { t } = useTranslation();
  return (
  <section className="py-24 bg-near-black/70 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] bg-blend-soft-light relative">
    <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-transparent to-near-black opacity-80"></div>
    <div className="max-w-7xl mx-auto px-6 relative z-10">
      <div className="flex flex-col md:flex-row justify-between items-center mb-16 gap-6 pt-16">
        <div className="text-center md:text-left">
          <p className="text-primary-light text-xs font-bold uppercase tracking-widest mb-2">{t('testimonials.title')}</p>
          <h2 className="text-3xl md:text-5xl font-display font-bold">{t('testimonials.sub')}</h2>
        </div>
        <button className="border border-white/20 hover:bg-white/5 px-6 py-2 rounded-full font-bold text-xs transition-all text-white/80">{t('testimonials.viewAll')}</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {(t('testimonials.items', { returnObjects: true }) as any[]).map((item, i) => (
          <div key={i} className="bg-charcoal/40 p-8 rounded-3xl flex flex-col border border-white/5 hover:border-primary-light/30 transition-all shadow-[0_0_20px_rgba(0,0,0,0.5)] group">
            <div className="flex gap-1 mb-6 text-primary-light">
              <Star size={16} fill="currentColor" /><Star size={16} fill="currentColor" /><Star size={16} fill="currentColor" /><Star size={16} fill="currentColor" /><Star size={16} fill="currentColor" />
            </div>
            <p className="text-white/80 text-sm mb-8 flex-grow leading-relaxed">"{item.text}"</p>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white font-bold group-hover:bg-primary-light group-hover:text-near-black transition-all">{item.name[0]}</div>
              <div>
                <p className="font-bold text-sm tracking-wide">{item.name}</p>
                <p className="text-[10px] uppercase tracking-widest text-white/80">{item.role}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
)};

export const Insights = () => {
  const { t, i18n } = useTranslation();
  const baseLang = (i18n.language || 'sq').split('-')[0];
  const lang = ['sq', 'en', 'de'].includes(baseLang) ? baseLang : 'sq';
  const articles = Object.values(blogContent as Record<string, any>).map((item: any) => item[lang]).filter(Boolean);
  const navigate = useNavigate();
  return (
  <section className="py-24 bg-near-black/70 relative overflow-hidden">
    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-light/5 rounded-full blur-[150px] mix-blend-screen pointer-events-none"></div>
    <div className="max-w-7xl mx-auto px-6 relative z-10 border-b border-white/5 pb-24">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6 pt-16">
        <div>
          <p className="text-primary-light text-xs font-bold uppercase tracking-widest mb-2">{t('insights.title')}</p>
          <h2 className="text-3xl md:text-5xl font-display font-medium leading-tight" dangerouslySetInnerHTML={{ __html: t('insights.subtitle').replace('&', '<br/>') }} />
        </div>
        <button onClick={() => navigate(localizedRoutesMap[lang]?.blog || '/sq/blog')} className="bg-white/5 hover:bg-white/10 px-8 py-3 rounded-xl font-bold text-sm transition-all border border-white/5 shadow-inner">{t('insights.viewAll')}</button>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {articles.slice(0, 3).map((article: any, i: number) => (
          <Link key={i} to={localizedRoutesMap[lang]?.articles?.replace(':title', article.slug) || `/sq/artikujt/${article.slug}`} className="group cursor-pointer">
            <div className="relative rounded-3xl overflow-hidden mb-6 bg-charcoal/80 aspect-video border border-white/5 group-hover:border-primary-light/50 transition-all shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
               <img src={article.image} alt={article.title} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 mix-blend-screen" loading="lazy" />
               <div className="absolute inset-0 bg-gradient-to-t from-near-black via-transparent to-transparent"></div>
               <div className="absolute top-4 left-4">
                  <span className="bg-primary/80 blur-0 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold text-white shadow-lg">{article.tag}</span>
               </div>
            </div>
            <h3 className="text-2xl font-bold mb-4 group-hover:text-primary-light transition-colors leading-tight">{article.title}</h3>
            <p className="text-white/80 text-[10px] font-bold uppercase tracking-widest">{article.date}</p>
          </Link>
        ))}
      </div>
    </div>
  </section>
)};
